import { BASE_URL } from '../components/BaseUrl';
import { decryptToken } from '../Utlis/Encryption';
import { apiGet, apiPost, apiPut, apiAuthFetch } from '../utils/apiService';

/**
 * Get the authentication token from Redux store or localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  return decryptToken(token);
};

/**
 * Messaging API Service
 * Handles all messaging-related API calls
 */
const messagingApi = {
  /**
   * Get eligible chat users
   * @param {Object} params - Query parameters (search, page, limit, role, noPagination)
   * @returns {Promise} API response
   */
  getEligibleUsers: async (params = {}) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.role) queryParams.append('role', params.role);
    if (params.noPagination) queryParams.append('noPagination', params.noPagination);

    const url = `${BASE_URL}/messages/eligible-users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Get all threads (inbox)
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} API response
   */
  getThreads: async (params = {}) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${BASE_URL}/messages/threads${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Get threads filtered by role
   * @param {string} role - Role to filter by (worker, account_executive, superadmin)
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} API response
   */
  getThreadsByRole: async (role, params = {}) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const queryParams = new URLSearchParams();
    queryParams.append('role', role);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${BASE_URL}/messages/threads/by-role?${queryParams.toString()}`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Create or get thread with a user
   * @param {number} receiverId - ID of the receiver
   * @param {string} [receiverRole] - Role: worker | company_admin | superadmin | account_executive
   * @returns {Promise} API response
   */
  createThread: async (receiverId, receiverRole) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads`;
    return apiAuthFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ receiverId, receiverRole: receiverRole || 'worker' }),
    }, token);
  },

  /**
   * Get thread details
   * @param {number} threadId - Thread ID
   * @returns {Promise} API response
   */
  getThreadDetails: async (threadId) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads/${threadId}`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Get messages in a thread
   * @param {number} threadId - Thread ID
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} API response
   */
  getMessages: async (threadId, params = {}) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${BASE_URL}/messages/threads/${threadId}/messages${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Send a text message
   * @param {number} threadId - Thread ID
   * @param {string} content - Message content
   * @param {string} messageType - Message type (text, image, pdf, audio)
   * @returns {Promise} API response
   */
  sendMessage: async (threadId, content, messageType = 'text') => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads/${threadId}/messages`;
    return apiAuthFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, messageType }),
    }, token);
  },

  /**
   * Send a message with file (image, PDF, audio)
   * @param {number} threadId - Thread ID
   * @param {FormData} formData - FormData with content, messageType, and file
   * @returns {Promise} API response
   */
  sendMessageWithFile: async (threadId, formData) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads/${threadId}/messages`;
    
    // For FormData, we need to use fetch directly
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    return {
      ...data,
      ok: response.ok,
      status: response.status,
    };
  },

  /**
   * Mark messages as read
   * @param {number} threadId - Thread ID
   * @param {number|null} upToMessageId - Optional message ID to mark up to
   * @returns {Promise} API response
   */
  markAsRead: async (threadId, upToMessageId = null) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads/${threadId}/read`;
    const body = upToMessageId ? { upToMessageId } : {};
    
    return apiAuthFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }, token);
  },

  /**
   * Mark all messages in thread as read
   * @param {number} threadId - Thread ID
   * @returns {Promise} API response
   */
  markAllAsRead: async (threadId) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/threads/${threadId}/read-all`;
    return apiAuthFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Mark all messages as read (global) - PUT /api/messages/mark-all-read
   * @returns {Promise} API response
   */
  markAllMessagesRead: async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/mark-all-read`;
    return apiAuthFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    }, token);
  },

  /**
   * Get detailed unread counts - GET /api/messages/unread-counts/detailed
   * Response: { total, threads: { threadId: count }, roleBased: { account_executive, company_admin, worker, super_admin } }
   * @returns {Promise} API response
   */
  getDetailedUnreadCounts: async () => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/unread-counts/detailed`;
    return apiAuthFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, token);
  },

  /**
   * Send broadcast message to multiple workers
   * @param {Array<number>} workerIds - Array of worker IDs
   * @param {string} content - Message content
   * @param {string} messageType - Message type (default: 'text')
   * @returns {Promise} API response
   */
  sendBroadcast: async (workerIds, content, messageType = 'text') => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required');

    const url = `${BASE_URL}/messages/broadcast`;
    return apiAuthFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ workerIds, content, messageType }),
    }, token);
  },
};

export default messagingApi;

